// GameEngine.kt

@file:OptIn(ExperimentalJsExport::class)
@file:JsExport

package game

import game.three.AmbientLight
import game.three.BoxGeometry
import game.three.CanvasTexture
import game.three.Color
import game.three.CylinderGeometry
import game.three.DirectionalLight
import game.three.Group
import game.three.Mesh
import game.three.MeshStandardMaterial
import game.three.Object3D
import game.three.PerspectiveCamera
import game.three.PlaneGeometry
import game.three.Scene
import game.three.SphereGeometry
import game.three.Sprite
import game.three.SpriteMaterial
import game.three.Vector3
import game.three.WebGLRenderer
import web.device.devicePixelRatio
import web.html.HTMLDivElement
import web.html.HTMLCanvasElement
import kotlin.js.JsName
import kotlin.math.PI
import kotlin.math.atan2
import kotlin.math.cos
import kotlin.math.sin
import kotlin.math.sqrt

// 坦克渲染结构:车身与炮塔分开旋转
private class TankMeshes(
    val root: Group,        // 整体(定位)
    val body: Group,        // 车身(随 angle 旋转)
    val turret: Group,      // 炮塔+炮管(随 turretAngle 旋转)
    val shield: Mesh        // 护盾光罩(可见性切换)
)

/**
 * 管理 THREE.js 场景与渲染循环。
 * - 每帧读取键盘/鼠标输入,派发 Tick action
 * - 根据 Redux 状态同步坦克 / 子弹网格
 * - 相机跟随玩家,位于坦克后方
 */
class GameEngine(private val container: HTMLDivElement) {

    // ===== 使用 @JsName 固定 JS 属性名 =====
    
    @JsName("scene")
    val scene = Scene()
    
    @JsName("camera")
    val camera = PerspectiveCamera(GameConfig.CAMERA_FOV, 1.0, 0.05, 500.0)
    
    @JsName("renderer")
    val renderer = WebGLRenderer(jsoOf(antialias = true))
    
    @JsName("rafId")
    private var rafId: dynamic = null

    // 网格对象 (private 不需要 @JsName)
    private val tankMeshes = mutableMapOf<Int, TankMeshes>()
    private val bulletMeshes = mutableMapOf<Int, Mesh>()
    private val powerupMeshes = mutableMapOf<Int, Mesh>()
    private val laserMeshes = mutableMapOf<Int, Mesh>()
    private val effectMeshes = mutableMapOf<Int, Mesh>()
    private val damageNumberMeshes = mutableMapOf<Int, Sprite>()
    private val obstacleMeshes = mutableListOf<Mesh>()

    // 键盘输入状态
    private val keys = mutableSetOf<String>()

    // 鼠标状态(Pointer Lock 下的视角旋转)
    private var pointerLocked = false
    
    @JsName("cameraYaw")
    private var cameraYaw = 0.0      // 视角水平角(绕坦克)
    
    @JsName("cameraPitch")
    private var cameraPitch = 0.35   // 视角俯仰(俯视角度,弧度)
    
    @JsName("mouseAim")
    private var mouseAim = 0.0       // 当前炮塔瞄准角

    // 上次时间(用于计算 dt)
    private var lastTime = 0.0

    // 累计时间(用于道具悬浮动画)
    private var time = 0.0

    init {
        resize()
        container.appendChild<HTMLCanvasElement>(renderer.domElement as HTMLCanvasElement)

        scene.background = Color(0x111827)
        setupLights()
        setupGround()

        // ===== 🚀 挂载到 window 方便调试 =====
        val win: dynamic = js("window")
        win.__gameEngine = this
        console.log("🎮 GameEngine 已挂载到 window.__gameEngine")
        console.log("   📐 scene: window.__gameEngine.scene")
        console.log("   📷 camera: window.__gameEngine.camera")
        console.log("   🖼️ renderer: window.__gameEngine.renderer")
        console.log("   🔧 resize(): window.__gameEngine.resize()")
        console.log("   ▶️ start(): window.__gameEngine.start()")
    }

    private fun jsoOf(antialias: Boolean): dynamic {
        val obj: dynamic = js("({})")
        obj.antialias = antialias
        return obj
    }

    private fun setupLights() {
        val dirLight = DirectionalLight(0xffffff, 1.2)
        dirLight.position.set(20.0, 40.0, 20.0)
        scene.add(dirLight)

        val ambient = AmbientLight(0x404060, 0.6)
        scene.add(ambient)
    }

    private fun setupGround() {
        val groundGeo = PlaneGeometry(GameConfig.ARENA_SIZE, GameConfig.ARENA_SIZE)
        val groundMat = MeshStandardMaterial(jsoOf(color = 0x1f2937, roughness = 0.9))
        val ground = Mesh(groundGeo, groundMat)
        ground.rotation.set(-PI / 2, 0.0, 0.0)
        scene.add(ground)
    }

    private fun jsoOf(color: Int, roughness: Double): dynamic {
        val obj: dynamic = js("({})")
        obj.color = color
        obj.roughness = roughness
        return obj
    }

    private fun jsoOf(map: dynamic, transparent: Boolean, depthTest: Boolean): dynamic {
        val obj: dynamic = js("({})")
        obj.map = map
        obj.transparent = transparent
        obj.depthTest = depthTest
        return obj
    }

    /** 挂载障碍物网格 */
    @JsName("buildObstacles")
    fun buildObstacles(obstacles: List<ObstacleState>) {
        obstacleMeshes.forEach { scene.remove(it) }
        obstacleMeshes.clear()

        obstacles.forEach { obstacle ->
            val geo = BoxGeometry(obstacle.w, 2.0, obstacle.h)
            val mat = MeshStandardMaterial(jsoOf(color = 0x92400e, roughness = 0.7))
            val mesh = Mesh(geo, mat)
            mesh.position.set(obstacle.x, 1.0, obstacle.z)
            scene.add(mesh)
            obstacleMeshes.add(mesh)
        }
    }

    /** 启动渲染循环 */
    @JsName("start")
    fun start() {
        buildObstacles(gameStore.getState().obstacles)
        lastTime = 0.0
        val state = gameStore.getState()
        if (state.status == GameStatus.PLAYING) {
            cameraYaw = state.player.angle
            mouseAim = state.player.turretAngle
        }
        loop(0.0)
    }

    private fun loop(time: Double) {
        val dt = if (lastTime == 0.0) 0.016 else ((time - lastTime) / 1000.0).coerceIn(0.0, 0.05)
        lastTime = time
        this.time = time / 1000.0

        val state = gameStore.getState()
        // P 键切换暂停
        if (consumeKey("p", "P")) {
            if (state.status == GameStatus.PLAYING) {
                gameStore.dispatch(GameAction.Pause)
            } else if (state.status == GameStatus.PAUSED) {
                gameStore.dispatch(GameAction.Resume)
            }
        }
        if (state.status == GameStatus.PLAYING) {
            // 炮塔瞄准角 = 视角朝向(与相机绕坦克的角度一致)
            // Q/E 控制相机俯仰
            if (keys.any { it in setOf("q", "Q") }) {
                cameraPitch += GameConfig.PITCH_SPEED * dt
            }
            if (keys.any { it in setOf("e", "E") }) {
                cameraPitch -= GameConfig.PITCH_SPEED * dt
            }
            cameraPitch = cameraPitch.coerceIn(GameConfig.PITCH_MIN, GameConfig.PITCH_MAX)

            val input = PlayerInput(
                forward = keys.any { it in setOf("w", "W", "ArrowUp") },
                backward = keys.any { it in setOf("s", "S", "ArrowDown") },
                left = keys.any { it in setOf("a", "A", "ArrowLeft") },
                right = keys.any { it in setOf("d", "D", "ArrowRight") },
                fire = keys.contains(" "),
                jump = keys.any { it in setOf("Shift", "ShiftLeft", "ShiftRight") },
                dash = consumeKey("t", "T"),
                shield = consumeKey("y", "Y"),
                nuke = consumeKey("u", "U"),
                aimAngle = mouseAim
            )
            gameStore.dispatch(GameAction.Tick(input, dt))
        }

        sync(state)
        renderer.render(scene, camera)
        rafId = requestAnimationFrame(::loop)
    }

    // 按键边缘触发(按下一次仅触发一次)
    private fun consumeKey(vararg names: String): Boolean {
        val pressed = names.any { keys.contains(it) }
        if (pressed) {
            names.forEach { keys.remove(it) }
        }
        return pressed
    }

    // 上次构建的障碍物签名
    private var obstacleKey = ""

    /** 根据 Redux 状态同步网格 */
    private fun sync(state: GameState) {
        // 障碍物变化时重建网格(每波随机生成)
        val key = state.obstacles.joinToString { "${it.id}:${it.x}:${it.z}:${it.w}:${it.h}" }
        if (key != obstacleKey) {
            buildObstacles(state.obstacles)
            obstacleKey = key
        }
        syncTanks(state.player, state.enemies)
        syncBullets(state.bullets)
        syncPowerups(state.powerups)
        syncLasers(state.lasers)
        syncEffects(state.effects)
        syncDamageNumbers(state.damageNumbers)

        // 未锁定鼠标时,视角跟随底盘转向(与炮塔一起)
        if (!pointerLocked && state.status == GameStatus.PLAYING) {
            cameraYaw = state.player.angle
            mouseAim = state.player.angle
        }

        // 第三人称俯视:相机绕坦克旋转,能看到坦克模型与炮塔
        val p = state.player
        val camX = p.x - sin(cameraYaw) * GameConfig.CAMERA_DISTANCE
        val camZ = p.z - cos(cameraYaw) * GameConfig.CAMERA_DISTANCE
        val camY = p.y + GameConfig.CAMERA_HEIGHT
        camera.position.set(camX, camY, camZ)
        // 看向坦克上方(带俯视角度)
        val lookY = p.y + (GameConfig.CAMERA_HEIGHT - sin(cameraPitch) * GameConfig.CAMERA_HEIGHT)
        camera.lookAt(Vector3(p.x, lookY.coerceAtLeast(p.y + 0.5), p.z))
        camera.updateProjectionMatrix()
    }

    private fun syncTanks(player: TankState, enemies: List<TankState>) {
        val allTanks = listOf(player) + enemies
        val activeIds = allTanks.filter { it.alive }.map { it.id }.toSet()

        // 移除已消失的坦克
        tankMeshes.keys.filter { it !in activeIds }.forEach { id ->
            scene.remove(tankMeshes.remove(id)!!.root)
        }

        allTanks.filter { it.alive }.forEach { tank ->
            val meshes = tankMeshes.getOrPut(tank.id) {
                createTankMesh(tank).also { scene.add(it.root) }
            }
            // Y 轴高度(跳跃)
            meshes.root.position.set(tank.x, tank.y, tank.z)
            meshes.body.rotation.y = tank.angle
            meshes.turret.rotation.y = tank.turretAngle
            // 护盾特效
            meshes.shield.visible = tank.shieldTimer > 0.0
            // 第三人称:所有坦克(含玩家)可见
            meshes.root.visible = true
        }
    }

    private fun createTankMesh(tank: TankState): TankMeshes {
        val root = Group()

        // 车身(随 angle 旋转)
        val body = Group()
        val bodyGeo = BoxGeometry(2.6, 1.0, 3.4)
        val bodyMat = MeshStandardMaterial(jsoOf(color = if (tank.isPlayer) 0x2563eb else 0xdc2626, roughness = 0.6))
        val bodyMesh = Mesh(bodyGeo, bodyMat)
        bodyMesh.position.y = 0.8
        body.add(bodyMesh)
        root.add(body)

        // 炮塔 + 炮管(随 turretAngle 旋转,独立于车身)
        val turret = Group()
        val turretGeo = BoxGeometry(2.0, 0.8, 2.2)
        val turretMat = MeshStandardMaterial(jsoOf(color = if (tank.isPlayer) 0x3b82f6 else 0xef4444, roughness = 0.6))
        val turretMesh = Mesh(turretGeo, turretMat)
        turretMesh.position.y = 1.7
        turret.add(turretMesh)

        val barrelGeo = CylinderGeometry(0.2, 0.25, 2.6, 8)
        val barrelMat = MeshStandardMaterial(jsoOf(color = 0x374151, roughness = 0.4))
        val barrel = Mesh(barrelGeo, barrelMat)
        barrel.rotation.x = PI / 2
        barrel.position.set(0.0, 1.8, 2.2)
        turret.add(barrel)
        root.add(turret)

        // 护盾光罩(半透明)
        val shieldGeo = SphereGeometry(2.2, 16, 16)
        val shieldMat = MeshStandardMaterial(jsoOf(color = 0x38bdf8, roughness = 0.1))
        shieldMat.asDynamic().transparent = true
        shieldMat.asDynamic().opacity = 0.35
        val shieldMesh = Mesh(shieldGeo, shieldMat)
        shieldMesh.position.y = 1.2
        shieldMesh.visible = false
        root.add(shieldMesh)

        return TankMeshes(root = root, body = body, turret = turret, shield = shieldMesh)
    }

    private fun syncBullets(bullets: List<BulletState>) {
        val activeIds = bullets.map { it.id }.toSet()

        bulletMeshes.keys.filter { it !in activeIds }.forEach { id ->
            scene.remove(bulletMeshes.remove(id)!!)
        }

        bullets.forEach { bullet ->
            // 若类型变化则重建网格(类型改变时旧网格重新创建)
            val mesh = bulletMeshes.getOrPut(bullet.id) {
                val m = createBulletMesh(bullet.type)
                scene.add(m)
                m
            }
            val color = when (bullet.type) {
                BulletType.STANDARD -> 0xfacc15
                BulletType.LASER -> 0xef4444
                BulletType.MISSILE -> 0xf97316
                BulletType.HOMING -> 0xa855f7
                BulletType.MELEE -> 0x38bdf8
                BulletType.SHOTGUN -> 0xfbbf24
                BulletType.PLASMA -> 0x34d399
                BulletType.GATLING -> 0xfbbf24
                BulletType.EMP -> 0x22d3ee
                BulletType.GRENADE -> 0x84cc16
                BulletType.FLAME -> 0xf97316
                BulletType.OBLIVION -> 0x8b5cf6
            }
            mesh.asDynamic().material.color = Color(color)
            mesh.position.set(bullet.x, 1.0, bullet.z)
            // 等离子 / EMP 自发光
            val emissive = when (bullet.type) {
                BulletType.PLASMA -> 0x34d399
                BulletType.EMP -> 0x22d3ee
                BulletType.MISSILE -> 0xf97316
                BulletType.HOMING -> 0xa855f7
                BulletType.GRENADE -> 0x84cc16
                BulletType.FLAME -> 0xf97316
                else -> 0x000000
            }
            if (emissive != 0) {
                mesh.asDynamic().material.emissive = Color(emissive)
                mesh.asDynamic().material.emissiveIntensity = 0.8
            } else {
                mesh.asDynamic().material.emissive = Color(0x000000)
            }
            // 激光拉长
            if (bullet.type == BulletType.LASER) {
                mesh.scale.set(1.0, 1.0, 3.0)
            } else {
                mesh.scale.set(1.0, 1.0, 1.0)
            }
            // 导弹/追踪弹朝飞行方向倾斜
            if (bullet.type == BulletType.MISSILE || bullet.type == BulletType.HOMING) {
                val flyAngle = atan2(bullet.vx, bullet.vz)
                mesh.rotation.y = flyAngle
            }
        }
    }

    private fun createBulletMesh(type: BulletType): Mesh {
        val geo = when (type) {
            // 导弹:锥形弹身(前细后粗)
            BulletType.MISSILE -> CylinderGeometry(0.1, 0.3, 1.1, 8)
            BulletType.LASER -> BoxGeometry(0.2, 0.2, 1.0)
            BulletType.PLASMA -> SphereGeometry(0.45, 12, 12)
            BulletType.HOMING -> SphereGeometry(0.3, 8, 8)
            BulletType.SHOTGUN -> SphereGeometry(0.18, 6, 6)
            BulletType.GATLING -> BoxGeometry(0.12, 0.12, 0.5)
            BulletType.EMP -> SphereGeometry(0.4, 10, 10)
            BulletType.GRENADE -> SphereGeometry(0.35, 8, 8)
            BulletType.FLAME -> SphereGeometry(0.3, 8, 8)
            BulletType.OBLIVION -> {
                // 黑洞特效：多层球体 + 发光粒子
                SphereGeometry(2.0, 32, 32).also {
                    // 大尺寸、黑色内核、紫色光晕
                }
            }
            else -> SphereGeometry(0.3, 8, 8)
        }
        val mat = MeshStandardMaterial(jsoOf(color = 0xfacc15, roughness = 0.3))
        if (type == BulletType.OBLIVION) {
            mat.emissive = Color(0x7c3aed)
            mat.asDynamic().emissiveIntensity = 3.0
            mat.asDynamic().transparent = true
            mat.asDynamic().opacity = 0.9
        }
        return Mesh(geo, mat)
    }
    
    private fun createOblivionAura(position: Vector3): Group {
        val group = Group()
        // 外层紫色光环
        val ringGeo = SphereGeometry(3.0, 16, 16)
        val ringMat = MeshStandardMaterial(jsoOf(color = 0x7c3aed, roughness = 0.0))
        ringMat.asDynamic().transparent = true
        ringMat.asDynamic().opacity = 0.3
        ringMat.emissive = Color(0x7c3aed)
        ringMat.asDynamic().emissiveIntensity = 2.0
        val ring = Mesh(ringGeo, ringMat)
        group.add(ring)
        return group
    }

    private fun syncPowerups(powerups: List<PowerupState>) {
        val activeIds = powerups.map { it.id }.toSet()

        powerupMeshes.keys.filter { it !in activeIds }.forEach { id ->
            scene.remove(powerupMeshes.remove(id)!!)
        }

        powerups.forEach { powerup ->
            val mesh = powerupMeshes.getOrPut(powerup.id) {
                val geo = SphereGeometry(0.6, 12, 12)
                val color = when (powerup.type) {
                    PowerupType.HEALTH -> 0x22c55e
                    PowerupType.SPEED -> 0x38bdf8
                    PowerupType.SHIELD -> 0xfacc15
                    PowerupType.RAPID -> 0xf97316
                    PowerupType.MULTI -> 0xa855f7
                }
                val mat = MeshStandardMaterial(jsoOf(color = color, roughness = 0.2))
                // emissive 必须是 Color 对象,否则默认为黑色导致球体发黑
                mat.emissive = Color(color)
                mat.asDynamic().emissiveIntensity = 0.7
                val m = Mesh(geo, mat)
                scene.add(m)
                m
            }
            // 道具悬浮旋转
            mesh.position.set(powerup.x, 1.2 + sin(time * 2.0 + powerup.id) * 0.3, powerup.z)
            mesh.rotation.y += 0.03
        }
    }

    private fun syncLasers(lasers: List<LaserBeamState>) {
        val activeIds = lasers.map { it.id }.toSet()

        laserMeshes.keys.filter { it !in activeIds }.forEach { id ->
            scene.remove(laserMeshes.remove(id)!!)
        }

        lasers.forEach { laser ->
            val mesh = laserMeshes.getOrPut(laser.id) {
                // 竖直光柱:用细长盒体,水平放置于命中方向
                val geo = BoxGeometry(0.18, 0.18, 1.0)
                val mat = MeshStandardMaterial(jsoOf(color = 0xef4444, roughness = 0.1))
                mat.emissive = Color(0xff2222)
                mat.asDynamic().emissiveIntensity = 1.0
                val m = Mesh(geo, mat)
                scene.add(m)
                m
            }
            // 计算光束长度与朝向
            val dx = laser.x1 - laser.x0
            val dz = laser.z1 - laser.z0
            val length = sqrt(dx * dx + dz * dz).coerceAtLeast(0.1)
            val angle = atan2(dx, dz)
            mesh.position.set(
                (laser.x0 + laser.x1) / 2.0,
                1.0,
                (laser.z0 + laser.z1) / 2.0
            )
            mesh.rotation.set(0.0, angle, 0.0)
            mesh.scale.set(1.0, 1.0, length)
            // 发光透明度随生命周期渐隐
            val alpha = (laser.life / 0.15).coerceIn(0.0, 1.0)
            mesh.asDynamic().material.opacity = alpha
        }
    }

    private fun syncEffects(effects: List<EffectState>) {
        val activeIds = effects.map { it.id }.toSet()

        effectMeshes.keys.filter { it !in activeIds }.forEach { id ->
            scene.remove(effectMeshes.remove(id)!!)
        }

        effects.forEach { effect ->
            val mesh = effectMeshes.getOrPut(effect.id) {
                // 电击:竖直电柱;EMP:青色冲击波环
                val geo = when (effect.type) {
                    EffectType.MELEE_SHOCK -> CylinderGeometry(0.12, 0.12, 4.0, 8)
                    EffectType.EMP_BLAST -> SphereGeometry(effect.radius, 16, 16)
                }
                val mat = MeshStandardMaterial(jsoOf(color = 0xffffff, roughness = 0.1))
                mat.asDynamic().transparent = true
                when (effect.type) {
                    EffectType.MELEE_SHOCK -> {
                        mat.emissive = Color(0x38bdf8)
                        mat.asDynamic().emissiveIntensity = 2.5
                        mat.asDynamic().opacity = 0.9
                    }
                    EffectType.EMP_BLAST -> {
                        mat.emissive = Color(0x22d3ee)
                        mat.asDynamic().emissiveIntensity = 1.5
                        mat.asDynamic().opacity = 0.4
                    }
                }
                val m = Mesh(geo, mat)
                scene.add(m)
                m
            }
            val progress = 1.0 - (effect.life / 0.3).coerceIn(0.0, 1.0)  // 0→1 扩散
            if (effect.type == EffectType.MELEE_SHOCK) {
                // 竖直电柱:从地面直插天空(竖立,不旋转)
                mesh.rotation.set(0.0, 0.0, 0.0)
                mesh.position.set(effect.x, 2.0, effect.z)
                val h = 3.0 + effect.radius * (0.5 + progress * 0.5)
                mesh.scale.set(1.0, h, 1.0)
                mesh.asDynamic().material.opacity = 0.9 * (1.0 - progress)
            } else {
                // EMP 冲击波:球体扩散淡出
                mesh.position.set(effect.x, 1.0, effect.z)
                val s = 0.5 + progress * 1.5
                mesh.scale.set(s, s, s)
                mesh.asDynamic().material.opacity = 0.4 * (1.0 - progress)
            }
        }
    }

    // 伤害飘字:Canvas 纹理精灵
    private fun syncDamageNumbers(damageNumbers: List<DamageNumberState>) {
        val activeIds = damageNumbers.map { it.id }.toSet()

        damageNumberMeshes.keys.filter { it !in activeIds }.forEach { id ->
            scene.remove(damageNumberMeshes.remove(id)!!)
        }

        damageNumbers.forEach { d ->
            val sprite = damageNumberMeshes.getOrPut(d.id) {
                val canvas = createDamageCanvas(d)
                val texture = CanvasTexture(canvas)
                val material = SpriteMaterial(
                    jsoOf(map = texture, transparent = true, depthTest = false)
                )
                val s = Sprite(material)
                s.scale.set(3.0, 1.2, 1.0)
                scene.add(s)
                s
            }
            // 飘字位置 + 上升动画
            val rise = (1.0 - d.life) * 2.0
            sprite.position.set(d.x, 2.0 + rise, d.z)
            // 渐隐
            val alpha = (d.life / 1.0).coerceIn(0.0, 1.0)
            sprite.asDynamic().material.opacity = alpha
        }
    }

    private fun createDamageCanvas(d: DamageNumberState): dynamic {
        val canvas: dynamic = js("document.createElement('canvas')")
        canvas.width = 256
        canvas.height = 96
        val ctx: dynamic = canvas.getContext("2d")
        ctx.font = "bold 52px Arial"
        ctx.textAlign = "center"
        ctx.textBaseline = "middle"
        // 描边 + 填充
        ctx.strokeStyle = "rgba(0,0,0,0.9)"
        ctx.lineWidth = 6
        ctx.strokeText("-${d.amount}", 128, 48)
        ctx.fillStyle = if (d.isPlayer) "#facc15" else "#ef4444"
        ctx.fillText("-${d.amount}", 128, 48)
        return canvas
    }

    /** 键盘事件绑定 */
    @JsName("attachKeyListeners")
    fun attachKeyListeners() {
        val win: dynamic = js("window")
        win.addEventListener("keydown") { e ->
            val key: String = e.key ?: ""
            if (key == " ") e.preventDefault()
            keys.add(key)
        }
        win.addEventListener("keyup") { e ->
            val key: String = e.key ?: ""
            keys.remove(key)
        }
    }

    /** 鼠标事件绑定(Pointer Lock:点击进入,移动旋转视角,炮塔跟随) */
    @JsName("attachMouseListeners")
    fun attachMouseListeners() {
        val doc: dynamic = js("document")
        val canvas: dynamic = renderer.domElement

        // 点击画布进入指针锁定
        canvas.addEventListener("click") {
            canvas.requestPointerLock()
        }

        // 指针锁定状态变化
        doc.addEventListener("pointerlockchange") {
            pointerLocked = doc.pointerLockElement === canvas
            if (!pointerLocked) {
                // 解锁时初始化 mouseAim 为当前炮塔朝向,避免视角跳变
            }
        }

        // 指针锁定下移动:仅水平旋转视角(炮塔跟随),俯仰由 Q/E 控制
        doc.addEventListener("mousemove") { e ->
            if (pointerLocked) {
                val dx = (e.movementX as Double?) ?: 0.0
                cameraYaw -= dx * GameConfig.MOUSE_SENSITIVITY
                mouseAim = cameraYaw
            }
        }
    }

    /** 画面自适应:根据容器尺寸更新渲染器与相机宽高比 */
    @JsName("resize")
    fun resize() {
        val w = container.clientWidth
        val h = container.clientHeight
        if (w <= 0 || h <= 0) return

        renderer.setSize(w, h)
        renderer.setPixelRatio(devicePixelRatio)
        camera.aspect = w.toDouble() / h.toDouble()
        camera.updateProjectionMatrix()
    }

    @JsName("dispose")
    fun dispose() {
        cancelAnimationFrame(rafId)
        renderer.dispose()
    }

    private fun requestAnimationFrame(callback: (Double) -> Unit): dynamic {
        val win: dynamic = js("window")
        return win.requestAnimationFrame(callback)
    }

    private fun cancelAnimationFrame(id: dynamic) {
        val win: dynamic = js("window")
        win.cancelAnimationFrame(id)
    }
}